import { useState, useEffect, useCallback, useContext, useRef } from "react";
import api from '../api/axios';
import { useNavigate, useParams } from "react-router-dom";
import { toast, ToastContainer } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import { FaUser, FaCheck, FaTimes, FaTrash, FaArrowLeft, FaRedo } from "react-icons/fa";
import Select from "react-select";
import { UserContext } from "../context/UserContext";
import { useTranslation } from "../context/LanguageContext";
import '../styles/common.css';
import './ModifierUser.css';

export default function ModifierUser() {
  const { t } = useTranslation();
  const { id } = useParams();
  const navigate = useNavigate();
  const { user: currentUser } = useContext(UserContext);
  const [formData, setFormData] = useState({
    username: "",
    email: "",
    first_name: "",
    last_name: "",
    groups: [],
    excluded_permissions: [],
  });
  const [initialData, setInitialData] = useState(null);
  const [groups, setGroups] = useState([]);
  const [groupPermissions, setGroupPermissions] = useState([]); // Permissions des groupes sélectionnés
  const [selectedPermissions, setSelectedPermissions] = useState([]); // Permissions cochées
  const [loading, setLoading] = useState(true);
  const [loadingPermissions, setLoadingPermissions] = useState(false);
  const [fetchError, setFetchError] = useState(null);
  const [submitting, setSubmitting] = useState(false);
  const [showConfirmModal, setShowConfirmModal] = useState(false);
  const [emailError, setEmailError] = useState("");
  const initialExcludedPermsRef = useRef([]);

  // Fonction réutilisable pour charger l'utilisateur
  const fetchUser = useCallback(async () => {
    try {
      const response = await api.get(`/api/users/${id}/`);
      // Normaliser les groupes (peuvent être des IDs ou des objets)
      const normalizedGroups = response.data.groups 
        ? response.data.groups.map(g => typeof g === 'object' ? g.id : g)
        : [];
      
      // Récupérer les excluded_permissions si l'API les renvoie
      // Format: tableau d'IDs des permissions exclues
      const excludedPermissions = response.data.excluded_permissions || [];

      const normalizedData = {
        ...response.data,
        groups: normalizedGroups,
        excluded_permissions: excludedPermissions,
      };
      setFormData(normalizedData);
      setInitialData(JSON.parse(JSON.stringify(normalizedData)));
      // Capturer les excluded_permissions initiales dans une ref pour éviter les stale closures
      initialExcludedPermsRef.current = excludedPermissions;
      
      // Note: Les permissions seront pré-cochées après le chargement des groupes
      // dans le useEffect qui charge les permissions des groupes
      setFetchError(null);
      setLoading(false);
    } catch (error) {
      console.error("Erreur lors du chargement:", error);
      
      // Créer un message d'erreur détaillé
      let errorMessage = t.errorLoadingUser;
      if (error.response) {
        if (error.response.status === 404) {
          errorMessage = t.userNotFound;
        } else if (error.response.status === 403) {
          errorMessage = t.noPermissionUser;
        } else if (error.response.status >= 500) {
          errorMessage = t.serverError;
        } else {
          errorMessage = error.response.data?.detail || error.response.data?.message || errorMessage;
        }
      } else if (error.request) {
        errorMessage = t.connectionError;
      }
      
      setFetchError(errorMessage);
      setLoading(false);
    }
  }, [id]);

  useEffect(() => {
    fetchUser();
  }, [fetchUser]);

  // Charger la liste des groupes
  useEffect(() => {
    const fetchGroups = async () => {
      try {
        const groupsRes = await api.get("/api/groups/");
        const groupsData = Array.isArray(groupsRes.data) 
          ? groupsRes.data 
          : (groupsRes.data.results || []);
        setGroups(groupsData);
      } catch (err) {
        console.error("Erreur lors du chargement des groupes:", err);
      }
    };
    fetchGroups();
  }, []);

  // Charger les permissions des groupes sélectionnés
  useEffect(() => {
    const fetchGroupPermissions = async () => {
      if (formData.groups.length === 0) {
        setGroupPermissions([]);
        setSelectedPermissions([]);
        return;
      }

      setLoadingPermissions(true);
      try {
        // Charger les détails de chaque groupe sélectionné
        const groupDetailsPromises = formData.groups.map(groupId =>
          api.get(`/api/groups/${groupId}/`).catch(() => null)
        );
        
        const groupDetails = await Promise.all(groupDetailsPromises);
        
        // Collecter toutes les permissions uniques de tous les groupes
        // Format backend: { id, codename, full_codename, name }
        const allPermsMap = new Map(); // Utiliser Map pour éviter les doublons et garder les infos complètes
        groupDetails.forEach((groupDetail) => {
          if (groupDetail && groupDetail.data) {
            const group = groupDetail.data;
            // Les permissions sont dans le champ 'permissions' selon la doc backend
            const perms = group.permissions || [];
            perms.forEach(perm => {
              // Prioriser full_codename (ex: "core.add_user"), sinon codename (ex: "add_user")
              const permKey = perm.full_codename || perm.codename || perm.name || perm;
              
              if (!allPermsMap.has(permKey)) {
                // Utiliser le nom du backend si disponible, sinon générer à partir du codename
                const permName = perm.name || 
                  (permKey.replace(/\./g, ' ').replace(/\b\w/g, l => l.toUpperCase()));
                
                allPermsMap.set(permKey, {
                  codename: permKey, // Utiliser full_codename en priorité pour l'ID unique
                  name: permName,
                  id: perm.id, // Garder l'ID si disponible
                });
              }
            });
          }
        });

        // Convertir la Map en tableau
        const permissionsArray = Array.from(allPermsMap.values());

        setGroupPermissions(permissionsArray);

        // Pré-sélectionner les permissions : toutes sauf celles dans excluded_permissions
        setSelectedPermissions(prev => {
          // Si on charge pour la première fois et qu'on a des excluded_permissions initiales
          const initialExcluded = initialExcludedPermsRef.current;
          if (initialExcluded && initialExcluded.length > 0 && prev.length === 0) {
            // Cocher toutes les permissions sauf celles exclues initialement
            return permissionsArray
              .filter(perm => !initialExcluded.includes(perm.id))
              .map(perm => perm.codename);
          }
          
          // Si pas encore de sélection, pré-cocher toutes les permissions des groupes
          if (prev.length === 0) {
            return permissionsArray.map(p => p.codename);
          }
          
          // Sinon, conserver seulement celles encore disponibles dans les groupes sélectionnés
          return prev.filter(p => permissionsArray.some(ap => ap.codename === p));
        });
      } catch (err) {
        console.error("Erreur lors du chargement des permissions des groupes:", err);
        setGroupPermissions([]);
        setSelectedPermissions([]);
      } finally {
        setLoadingPermissions(false);
      }
    };

    fetchGroupPermissions();
  }, [formData.groups]);

  // Fonction de validation d'email
  const validateEmail = (email) => {
    if (!email || email.trim() === "") {
      return ""; // Pas d'erreur si le champ est vide (email optionnel)
    }
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailRegex.test(email)) {
      return "Veuillez entrer une adresse email valide (ex: john.doe@example.com)";
    }
    return "";
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData({
      ...formData,
      [name]: value,
    });
    
    // Validation en temps réel pour l'email
    if (name === "email") {
      const error = validateEmail(value);
      setEmailError(error);
    }
  };

  const handleBlur = (e) => {
    const { name, value } = e.target;
    
    // Valider l'email quand on quitte le champ
    if (name === "email") {
      const error = validateEmail(value);
      setEmailError(error);
    }
  };

  const hasChanges = () => {
    if (!initialData) return false;
    
    // Normaliser les valeurs pour la comparaison (null/undefined -> "")
    const normalize = (val) => (val === null || val === undefined) ? "" : String(val).trim();
    
    // Comparer les groupes (tableaux)
    const groupsEqual = JSON.stringify([...formData.groups].sort()) === 
                       JSON.stringify([...(initialData.groups || [])].sort());
    
    // Comparer les excluded_permissions : calculer les excluded actuelles et les comparer
    const allGroupPermissionIds = groupPermissions
      .filter(perm => perm.id)
      .map(perm => perm.id);
    
    const selectedPermissionIds = selectedPermissions
      .map(permCodename => {
        const perm = groupPermissions.find(p => p.codename === permCodename);
        return perm?.id;
      })
      .filter(id => id !== undefined);
    
    const currentExcludedIds = allGroupPermissionIds.filter(
      id => !selectedPermissionIds.includes(id)
    ).sort();
    
    const initialExcludedIds = (initialData.excluded_permissions || []).sort();
    const permissionsEqual = JSON.stringify(currentExcludedIds) === JSON.stringify(initialExcludedIds);
    
    return (
      normalize(formData.email) !== normalize(initialData.email) ||
      normalize(formData.first_name) !== normalize(initialData.first_name) ||
      normalize(formData.last_name) !== normalize(initialData.last_name) ||
      !groupsEqual ||
      !permissionsEqual
    );
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    if (!hasChanges()) {
      return;
    }
    
    // Valider l'email avant d'ouvrir le modal
    const emailValidationError = validateEmail(formData.email);
    setEmailError(emailValidationError);
    
    // Vérifier s'il y a des erreurs
    if (emailValidationError) {
      return;
    }
    
    setShowConfirmModal(true);
  };

  const handleConfirmSubmit = async () => {
    setSubmitting(true);
    try {
      // Calculer les permissions exclues (décochées) : toutes les permissions des groupes moins celles cochées
      const allGroupPermissionIds = groupPermissions
        .filter(perm => perm.id) // Garder seulement celles qui ont un ID
        .map(perm => perm.id);
      
      const selectedPermissionIds = selectedPermissions
        .map(permCodename => {
          // Trouver la permission correspondante pour récupérer son ID
          const perm = groupPermissions.find(p => p.codename === permCodename);
          return perm?.id;
        })
        .filter(id => id !== undefined); // Retirer les undefined
      
      const excludedPermissionIds = allGroupPermissionIds.filter(
        id => !selectedPermissionIds.includes(id)
      );

      // Fonction helper pour convertir les chaînes vides en null
      const emptyToNull = (value) => {
        return value && value.trim() !== "" ? value.trim() : null;
      };

      const dataToSubmit = {
        username: formData.username,
        email: emptyToNull(formData.email),
        first_name: emptyToNull(formData.first_name),
        last_name: emptyToNull(formData.last_name),
        groups: formData.groups,
        excluded_permissions: excludedPermissionIds, // IDs des permissions décochées
      };
      await api.put(`/api/users/${id}/`, dataToSubmit);
      setShowConfirmModal(false);
      toast.success("Utilisateur modifié avec succès !");
      // Navigate after a short delay to allow the toast to be seen
      setTimeout(() => {
      navigate("/users");
      }, 500);
    } catch (err) {
      console.error(err);
      const errorMessage = err.response?.data?.detail || 
                          err.response?.data?.message ||
                          Object.values(err.response?.data || {}).flat().join(", ") ||
                          "Une erreur s'est produite lors de la mise à jour de l'utilisateur.";
      toast.error(errorMessage);
      setShowConfirmModal(false);
    } finally {
      setSubmitting(false);
    }
  };

  const handleCancelConfirm = () => {
    setShowConfirmModal(false);
  };

  const handleRetry = () => {
    setFetchError(null);
    setLoading(true);
    fetchUser();
  };

  const handleDelete = async () => {
    if (window.confirm("Voulez-vous vraiment supprimer cet utilisateur ?")) {
      try {
        await api.delete(`/api/users/${id}/`);
        toast.success("Utilisateur supprimé avec succès !");
        // Navigate after a short delay to allow the toast to be seen
        setTimeout(() => {
        navigate("/users");
        }, 500);
      } catch (error) {
        console.error("Erreur lors de la suppression :", error);
        const errorMessage = error.response?.data?.detail || 
                            error.response?.data?.message ||
                            Object.values(error.response?.data || {}).flat().join(", ") ||
                            "Une erreur s'est produite lors de la suppression.";
        toast.error(errorMessage);
      }
    }
  };

  if (loading) {
    return (
      <div className="modifier-user-page">
        <div className="loading-container">
          <p className="loading-text">Chargement de l'utilisateur...</p>
        </div>
      </div>
    );
  }

  if (fetchError) {
    return (
      <div className="modifier-user-page">
        <div className="title-badge">
          <FaUser className="page-title-icon" aria-hidden="true" />
          <span>ERREUR</span>
        </div>
        <div className="error-container">
          <p className="error-message">{fetchError}</p>
          <div className="actions">
            <button
              type="button"
              className="save-but"
              onClick={handleRetry}
            >
              <FaRedo />
              <span>Réessayer</span>
            </button>
            <button
              type="button"
              className="cancel-but"
              onClick={() => navigate("/users")}
            >
              <FaArrowLeft />
              <span>Retour</span>
            </button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="modifier-user-page">
      <div className="title-badge">
        <FaUser className="page-title-icon" aria-hidden="true" />
        <span>MODIFIER L'UTILISATEUR {formData.username || id}</span>
      </div>

      <form onSubmit={handleSubmit} className="user-form">
        {/* Bannière d'information */}
        <div className="form-info-banner">
          <span className="info-icon">ℹ️</span>
          <div className="info-content">
            <strong>Champs obligatoires :</strong>
            <span className="required-indicator">*</span> Les champs marqués d'un astérisque rouge sont obligatoires.
          </div>
        </div>

        <div className="form-section-container">
          <div className="form">
            <label htmlFor="username">
              Nom d'utilisateur <span className="required-indicator">*</span>
            </label>
            <input
              type="text"
              id="username"
              name="username"
              value={formData.username}
              onChange={handleChange}
              placeholder="Nom d'utilisateur"
              required
              disabled
            />
          </div>

          <div className="form">
            <label htmlFor="first_name">Prénom</label>
            <input
              type="text"
              id="first_name"
              name="first_name"
              value={formData.first_name || ""}
              onChange={handleChange}
              placeholder="Prénom"
            />
          </div>

          <div className="form">
            <label htmlFor="last_name">Nom</label>
            <input
              type="text"
              id="last_name"
              name="last_name"
              value={formData.last_name || ""}
              onChange={handleChange}
              placeholder="Nom"
            />
          </div>

          <div className="form">
            <label htmlFor="email">Email</label>
            <input
              type="email"
              id="email"
              name="email"
              value={formData.email || ""}
              onChange={handleChange}
              onBlur={handleBlur}
              placeholder="Ex: john.doe@example.com"
              className={emailError ? "input-error" : ""}
            />
            {emailError && (
              <span className="field-error-message">{emailError}</span>
            )}
          </div>

          <div className="form">
            <label htmlFor="groups">Groupes</label>
            <Select
              name="groups"
              inputId="groups"
              isMulti
              value={formData.groups.map(groupId => {
                const group = groups.find(g => g.id === groupId);
                return group ? { value: group.id, label: group.name } : null;
              }).filter(Boolean)}
              onChange={(selectedOptions) => {
                const groupIds = selectedOptions ? selectedOptions.map(opt => opt.value) : [];
                setFormData({ ...formData, groups: groupIds });
                // Les permissions seront automatiquement rechargées via useEffect
              }}
              options={groups.map(group => ({
                value: group.id,
                label: group.name
              }))}
              classNamePrefix="react-select"
              placeholder="Sélectionner des groupes (optionnel)"
            />
          </div>
        </div>

        <div className="form-section-container">
          <h3 className="form-section-title">Permissions</h3>
          <p className="form-section-description">
            Les permissions affichées proviennent automatiquement des groupes sélectionnés ci-dessus.
          </p>
          
          <div className="permissions-container">
            {loadingPermissions ? (
              <p className="no-permissions-message">Chargement des permissions...</p>
            ) : formData.groups.length === 0 ? (
              <p className="no-permissions-message">
                Sélectionnez d'abord un ou plusieurs groupes pour voir leurs permissions.
              </p>
            ) : (() => {
              // Fonction pour extraire le nom du module depuis un codename
              const extractModuleName = (codename) => {
                // Si format "app.action_model" (ex: "core.add_animal")
                if (codename.includes('.')) {
                  const parts = codename.split('.');
                  if (parts.length >= 2) {
                    // Prendre la partie après l'action (ex: "add_animal" -> "animal")
                    const actionModel = parts[1];
                    // Extraire le modèle en enlevant les préfixes d'actions
                    const modelName = actionModel
                      .replace(/^(add|change|delete|view)_/, '')
                      .replace(/_/g, ' ');
                    return modelName.charAt(0).toUpperCase() + modelName.slice(1);
                  }
                  return parts[0].charAt(0).toUpperCase() + parts[0].slice(1);
                }
                
                // Si format "action_model" (ex: "add_animal")
                const modelName = codename
                  .replace(/^(add|change|delete|view)_/, '')
                  .replace(/_/g, ' ');
                return modelName.charAt(0).toUpperCase() + modelName.slice(1);
              };

              // Fonction pour formater le nom d'une permission
              const formatPermissionName = (codename) => {
                // Si format "app.action_model"
                if (codename.includes('.')) {
                  const parts = codename.split('.');
                  if (parts.length >= 2) {
                    const action = parts[1].split('_')[0]; // "add", "change", etc.
                    const model = parts[1].replace(/^(add|change|delete|view)_/, '').replace(/_/g, ' ');
                    const actionLabels = {
                      'add': 'Ajouter',
                      'change': 'Modifier',
                      'delete': 'Supprimer',
                      'view': 'Voir',
                    };
                    return `${actionLabels[action] || action} ${model}`;
                  }
                }
                
                // Si format "action_model"
                const parts = codename.split('_');
                if (parts.length >= 2) {
                  const action = parts[0];
                  const model = parts.slice(1).join(' ');
                  const actionLabels = {
                    'add': 'Ajouter',
                    'change': 'Modifier',
                    'delete': 'Supprimer',
                    'view': 'Voir',
                  };
                  return `${actionLabels[action] || action} ${model}`;
                }
                
                return codename.replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase());
              };

              // Organiser les permissions par module
              const organizedPermissions = groupPermissions.reduce((acc, perm) => {
                const codename = perm.codename || perm.name || perm;
                const moduleName = extractModuleName(codename);
                
                if (!acc[moduleName]) acc[moduleName] = [];
                acc[moduleName].push({
                  codename: codename,
                  name: formatPermissionName(codename),
                });
                return acc;
              }, {});

              return Object.keys(organizedPermissions).length > 0 ? (
                Object.entries(organizedPermissions)
                  .sort(([a], [b]) => a.localeCompare(b)) // Trier par ordre alphabétique
                  .map(([module, perms]) => (
                  <div key={module} className="permission-group">
                    <h4 className="permission-group-title">{module}</h4>
                    <div className="permissions-checkboxes">
                      {perms.map((perm) => (
                        <label key={perm.codename} className="permission-checkbox">
                          <input
                            type="checkbox"
                            checked={selectedPermissions.includes(perm.codename)}
                            onChange={(e) => {
                              if (e.target.checked) {
                                setSelectedPermissions(prev => [...prev, perm.codename]);
                              } else {
                                setSelectedPermissions(prev => prev.filter(p => p !== perm.codename));
                              }
                            }}
                          />
                          <span>{perm.name}</span>
                        </label>
                      ))}
                    </div>
                  </div>
                ))
              ) : (
                <p className="no-permissions-message">
                  Les groupes sélectionnés n'ont pas de permissions assignées.
                </p>
              );
            })()}
          </div>
        </div>

        <div className="actions">
          <button
            type="submit"
            className="save-but"
            disabled={!hasChanges()}
          >
            <FaCheck />
            <span>Enregistrer</span>
          </button>
          <button
            type="button"
            className="cancel-but"
            onClick={() => navigate("/users")}
          >
            <FaTimes />
            <span>Annuler</span>
          </button>
          <button
            type="button"
            className="delete-but"
            onClick={handleDelete}
          >
            <FaTrash />
            <span>Supprimer</span>
          </button>
        </div>
      </form>

      {/* Modal de confirmation */}
      {showConfirmModal && (
        <div className="confirm-modal-overlay" onClick={handleCancelConfirm}>
          <div className="confirm-modal" onClick={(e) => e.stopPropagation()}>
            <div className="confirm-modal-header">
              <FaCheck className="confirm-modal-icon" />
              <h3>Confirmer la modification</h3>
            </div>
            <div className="confirm-modal-body">
              <p className="confirm-intro">
                Veuillez vérifier les modifications avant de confirmer :
              </p>
              <div className="confirm-summary">
                <div className="summary-row">
                  <span className="summary-label">Nom d'utilisateur :</span>
                  <span className="summary-value">{formData.username || "Non renseigné"}</span>
                </div>
                {formData.first_name && (
                  <div className="summary-row">
                    <span className="summary-label">Prénom :</span>
                    <span className="summary-value">{formData.first_name}</span>
                  </div>
                )}
                {formData.last_name && (
                  <div className="summary-row">
                    <span className="summary-label">Nom :</span>
                    <span className="summary-value">{formData.last_name}</span>
                  </div>
                )}
                {formData.email && (
                  <div className="summary-row">
                    <span className="summary-label">Email :</span>
                    <span className="summary-value">{formData.email}</span>
                  </div>
                )}
                <div className="summary-row">
                  <span className="summary-label">Groupes :</span>
                  <span className="summary-value">
                    {formData.groups && formData.groups.length > 0
                      ? formData.groups.map(groupId => {
                          const group = groups.find(g => g.id === groupId);
                          return group ? group.name : null;
                        }).filter(Boolean).join(", ") || "Aucun"
                      : "Aucun"}
                  </span>
                </div>
                <div className="summary-row">
                  <span className="summary-label">Permissions :</span>
                  <span className="summary-value">
                    {selectedPermissions.length > 0
                      ? `${selectedPermissions.length} permission(s) sélectionnée(s)`
                      : "Aucune permission sélectionnée"}
                  </span>
                </div>
              </div>
            </div>
            <div className="confirm-modal-footer">
              <button
                type="button"
                onClick={handleCancelConfirm}
                className="confirm-modal-cancel"
                disabled={submitting}
              >
                <FaTimes />
                <span>Annuler</span>
              </button>
              <button
                type="button"
                onClick={handleConfirmSubmit}
                className="confirm-modal-confirm"
                disabled={submitting}
              >
                <FaCheck />
                <span>{submitting ? "Enregistrement..." : "Confirmer"}</span>
              </button>
            </div>
          </div>
        </div>
      )}

      <ToastContainer
        position="top-right"
        autoClose={3000}
        hideProgressBar={false}
        newestOnTop={false}
        closeOnClick
        rtl={false}
        pauseOnFocusLoss
        draggable
        pauseOnHover
        theme="light"
      />
    </div>
  );
}